import { Trash2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import { formatSecretTimestamp } from './secret-dashboard-utils'
import type { SecretEntry } from './secret-dashboard-types'

interface SecretListProps {
  secrets: SecretEntry[]
  selectedId: string | null
  isLoading: boolean
  error: Error | null
  isDark: boolean
  onSelect: (entry: SecretEntry) => void
  onDisable: (entry: SecretEntry) => void
}

export function SecretList({ secrets, selectedId, isLoading, error, isDark, onSelect, onDisable }: SecretListProps) {
  const border = isDark ? 'border-white/[0.06]' : 'border-neutral-200'

  return (
    <div className={cn('overflow-x-auto rounded-md border', border)}>
      <table className="w-full min-w-[760px] text-left text-sm">
        <thead className={isDark ? 'bg-surface-1 text-neutral-400' : 'bg-neutral-100 text-neutral-500'}>
          <tr>
            <th className="px-3 py-2">Key</th>
            <th className="px-3 py-2">Kind</th>
            <th className="px-3 py-2">Value</th>
            <th className="px-3 py-2">Version</th>
            <th className="px-3 py-2">Updated</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr><td className="px-3 py-8 text-center text-neutral-500" colSpan={6}>Loading secrets...</td></tr>
          ) : error ? (
            <tr><td className="px-3 py-8 text-center text-red-500" colSpan={6}>{error.message}</td></tr>
          ) : secrets.length === 0 ? (
            <tr><td className="px-3 py-8 text-center text-neutral-500" colSpan={6}>No secrets match these filters.</td></tr>
          ) : secrets.map((item) => (
            <tr key={item.id} className={cn('border-t', border, selectedId === item.id && (isDark ? 'bg-brand-500/10' : 'bg-brand-50'))}>
              <td className="px-3 py-2">
                <button type="button" onClick={() => onSelect(item)} className="font-mono text-xs text-left hover:text-brand-500">
                  {item.key}
                </button>
                {item.description ? <div className="mt-0.5 max-w-xs truncate text-xs text-neutral-500">{item.description}</div> : null}
              </td>
              <td className="px-3 py-2">{item.kind}</td>
              <td className="px-3 py-2 font-mono text-xs">{item.valueMasked ? '*** masked ***' : item.value}</td>
              <td className="px-3 py-2">v{item.currentVersion}</td>
              <td className="px-3 py-2 text-xs text-neutral-500">{formatSecretTimestamp(item.updatedAt)}</td>
              <td className="px-3 py-2 text-right">
                <button type="button" onClick={() => onSelect(item)} className="rounded-md px-2 py-1 text-brand-600 hover:bg-brand-500/10">Manage</button>
                <button type="button" onClick={() => onDisable(item)} className="ml-1 rounded-md p-1 text-red-500 hover:bg-red-500/10" title="Disable secret" aria-label={`Disable ${item.key}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
